import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { getServiceListingTypes, serviceSearchQuery } from '../../../../util/serviceSearch';
import Field, { hasDataInFields } from '../../Field';
import { NamedLink } from '../../../../components';

import SectionContainer from '../SectionContainer';
import HeroTrustStrip from './HeroTrustStrip';
import HeroSpecialistsFeed, { HeroSpecialistsFeedSkeleton } from './HeroSpecialistsFeed';
import { heroImage } from './heroImage';
import { MIN_CARDS } from '../../../LandingPage/heroSpecialists';
import css from './SectionHero.module.css';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @param {Array<Object>?} props.options.heroSpecialists hero feed cards (D40, LandingPage)
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const isPrimaryHeroSection = sectionId === 'section-1';
  // D40: the feed replaces the photo when there are enough cards; while it loads (null: not
  // fetched yet, e.g. SPA navigation to / with an empty store) skeleton cards hold the slot; after a
  // failed or slow fetch, or with fewer cards, the photo stays. Same fixed-height slot in all cases.
  // Pages without the feed option (undefined) keep the photo.
  const heroSpecialists = options?.heroSpecialists;
  const showSpecialistsFeed = Array.isArray(heroSpecialists) && heroSpecialists.length >= MIN_CARDS;
  const isFeedLoading = heroSpecialists === null;
  // Service search per category; listing types from the runtime config (D22, T15).
  const serviceTypes = getServiceListingTypes(useConfiguration());
  // T26: this DOM order wraps the chips into 4 rows at ≥1536 and adds no row at any width from
  // 768px; re-measure the rows before reordering.
  // A9: on mobile (≤767px) the hero shows the chips flagged `mobile` plus «Все категории»
  // (→ #all-categories); the rest stay in the markup and appear from 768px.
  const quickCategories = [
    {
      label: 'Релокация и Адаптация',
      categoryLevel1: 'SvcCat_REL',
      mobile: true,
    },
    {
      label: 'Дом, Ремонт и Помощь в быту',
      categoryLevel1: 'SvcCat_HOM',
    },
    {
      label: 'Юридические услуги',
      categoryLevel1: 'SvcCat_LEGL',
      mobile: true,
    },
    {
      label: 'Недвижимость и Жильё',
      categoryLevel1: 'SvcCat_REH',
      mobile: true,
    },
    {
      label: 'Финансы и Налоги',
      categoryLevel1: 'SvcCat_TAX',
      mobile: true,
    },
    {
      label: 'Бизнес, Консалтинг и Digital / IT',
      categoryLevel1: 'SvcCat_BUS',
    },
    {
      label: 'Путешествия, Досуг и Мероприятия',
      categoryLevel1: 'SvcCat_LSR',
    },
    {
      label: 'Переводы',
      categoryLevel1: 'SvcCat_TRN',
    },
    {
      label: 'Красота, Спорт и Здоровье',
      categoryLevel1: 'SvcCat_BSH',
      mobile: true,
    },
    {
      label: 'Образование и Развитие',
      categoryLevel1: 'SvcCat_EDU',
      mobile: true,
    },
    {
      label: 'Авто, Перевозки и Курьеры',
      categoryLevel1: 'SvcCat_TRA',
    },
    {
      label: 'Животные и Питомцы',
      categoryLevel1: 'SvcCat_PETS',
    },
  ].map(category => ({
    ...category,
    search: serviceSearchQuery(serviceTypes, { categoryLevel1: category.categoryLevel1 }),
  }));

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields && isPrimaryHeroSection ? (
        <div
          className={css.primaryHeroLayout}
        >
          <div className={css.primaryHeroColumn}>
            <header className={css.primaryHeroContent}>
              <h1 className={classNames(defaultClasses.title, css.primaryHeroTitle)}>
                {/* D10: coral brand word (brand-name exception), not the SVG logo. The space between the
                  block spans renders nothing but keeps «DELOWO поиск…» apart in the text (A1-01). */}
                <span className="delowo-text--hero-brand">DELOWO</span>{' '}
                <span className="delowo-text--hero-title">
                  поиск русскоязычных специалистов в Европе
                </span>
              </h1>
              {/* D15 caption: a lead line under the headline, above the search (all widths) */}
              <p className={css.primaryHeroLead}>
                <FormattedMessage id="SectionHero.caption" />
              </p>
              <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
              <div className={classNames(css.quickLinksWrapper, css.primaryHeroQuickLinks)}>
                {quickCategories.map(category => (
                  <NamedLink
                    key={category.label}
                    name="SearchPage"
                    to={{ search: category.search }}
                    className={classNames('delowo-chip', css.heroChip, {
                      [css.chipTabletUp]: !category.mobile,
                    })}
                  >
                    {category.label}
                  </NamedLink>
                ))}
                <NamedLink
                  name="LandingPage"
                  to={{ hash: '#all-categories' }}
                  className={classNames('delowo-chip', css.heroChip, css.chipMobileOnly)}
                >
                  <FormattedMessage id="SectionHero.allCategories" />
                </NamedLink>
              </div>
              <HeroTrustStrip className={css.trustInColumn} />
            </header>
          </div>

          <div className={css.primaryHeroMediaColumn}>
            <div className={css.mediaSlot}>
              {showSpecialistsFeed ? (
                <HeroSpecialistsFeed specialists={heroSpecialists} serviceTypes={serviceTypes} />
              ) : isFeedLoading ? (
                <HeroSpecialistsFeedSkeleton />
              ) : (
                // L1: the LCP image on desktop, fetched first; decorative (alt=""), the h1 carries the meaning (A1-11)
                <picture className={css.primaryHeroPicture}>
                  <source type="image/avif" srcSet={heroImage.avif} sizes={heroImage.sizes} />
                  <source type="image/webp" srcSet={heroImage.webp} sizes={heroImage.sizes} />
                  <img
                    src={heroImage.src}
                    srcSet={heroImage.jpg}
                    sizes={heroImage.sizes}
                    width={heroImage.width}
                    height={heroImage.height}
                    fetchpriority="high"
                    alt=""
                    className={css.primaryHeroImage}
                  />
                </picture>
              )}
            </div>
            {/* ≥1024px: the trust strip sits under the feed (or the photo), 2×2 (D40) */}
            <HeroTrustStrip className={css.trustInMedia} vertical />
          </div>
        </div>
      ) : hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHero;
